// MaterialInspector — PlayCanvas-style collapsible sections for editing a
// material asset. Sections render based on the material's `type`:
//
//   pbr  → Ambient · Diffuse · Specular(Metalness) · Normals · Emissive · Opacity · Advanced
//   toon → Diffuse · Toon · Advanced
//   skin → Diffuse · Normals · Subsurface · Emissive · Advanced
//
// Edits PATCH the row on the server with a small debounce. Texture slots
// use TextureSlotField (drop-target + click-to-pick popup).

import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import TextureSlotField from './TextureSlotField'
import * as history from '../../lib/materialHistory'
import { reloadMaterialsEverywhere } from '../../lib/materialReloadBus'
import { collectExposedParams } from '../../lib/materialGraphCompiler'
import { isMaterialGraphConvertEnabled } from '../../lib/featureFlags'

const C = {
  bg:     '#0c0e15',
  bgRow:  '#11131b',
  border: '#1f2129',
  text:   '#e1e1ed',
  dim:    '#8f8fa2',
  accent: '#8b6cff',
  ok:     '#4caf82',
}

export default function MaterialInspector({ asset, onNavigateToAsset, onOpenGraph }) {
  const [data, setData] = useState(asset.data ?? {})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [conflict, setConflict] = useState(null)
  const debounceRef = useRef(null)
  // True while we hold an unsaved local edit (between set() and its debounced
  // PATCH). Guards the external-adopt effect from clobbering in-progress edits.
  const dirtyRef = useRef(false)
  const prevIdRef = useRef(asset.id)
  // Pending patch data when a conflict occurs — so "Overwrite" can retry.
  const pendingPatchRef = useRef(null)

  // Keep local state in sync with the asset.
  //   • asset switch  → hard reset to the new asset's data.
  //   • same asset, data changed externally (e.g. the graph was edited in the
  //     Material Graph modal → a new exposed parameter) → adopt it, UNLESS we
  //     have an unsaved local edit. Without this the exposed-params list went
  //     stale until a full page reload (F5).
  useEffect(() => {
    const switched = prevIdRef.current !== asset.id
    prevIdRef.current = asset.id
    if (switched) {
      dirtyRef.current = false
      setData(asset.data ?? {})
      setError(null)
      return
    }
    if (dirtyRef.current) return
    setData(prev => {
      const next = asset.data ?? {}
      if (prev === next) return prev
      try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev } catch { /* adopt */ }
      return next
    })
  }, [asset.id, asset.data])

  // True if at least one field in the pending patch required a full rebuild
  // (texture-slot binding, ORM toggle, class promotion, blend mode, …). The
  // debounced PATCH then calls __RELOAD_MATERIALS__; otherwise the live
  // preview already applied the change and we skip the reload to avoid a
  // texture-load flicker.
  const needsReloadRef = useRef(false)

  const set = (key, value) => {
    // 0) Undo snapshot — captured outside setData so the _notify() listener
    //    fan-out doesn't fire during a render (React's "setState in render"
    //    warning). recordEdit coalesces same-key edits within COALESCE_MS so
    //    dragging a slider is one undo step, not hundreds.
    history.recordEdit(asset.rawId, data, key)
    dirtyRef.current = true
    setData(prev => {
      const next = { ...prev, [key]: value }
      // 1) Real-time avatar preview — mutate the THREE material directly so
      //    sliders update the 3D view *during* the drag, not after release.
      //    livePreviewMaterial returns false when the field needs a rebuild
      //    (texture binding, class promotion, etc.); in that case fall back
      //    to the full reload on the debounced PATCH.
      let applied = false
      if (typeof window !== 'undefined' && typeof window.__LIVE_PREVIEW_MATERIAL__ === 'function') {
        applied = window.__LIVE_PREVIEW_MATERIAL__(asset.rawId, { [key]: value })
      }
      if (!applied) needsReloadRef.current = true
      // 2) Server persistence — debounced.
      schedulePatch({ data: next })
      return next
    })
  }

  // Restore a full data snapshot (from undo/redo). Mirrors `set` but
  // replaces the entire data record at once and forces a full reload on
  // PATCH (since we can't tell which fields changed without diffing —
  // simpler to just rebuild).
  const restoreSnapshot = (nextData) => {
    if (!nextData) return
    dirtyRef.current = true
    setData(nextData)
    // Live-preview the *fields* we know how to mutate; flag rebuild for the
    // rest so the reload picks them up.
    if (typeof window !== 'undefined' && typeof window.__LIVE_PREVIEW_MATERIAL__ === 'function') {
      const ok = window.__LIVE_PREVIEW_MATERIAL__(asset.rawId, nextData)
      if (!ok) needsReloadRef.current = true
    } else {
      needsReloadRef.current = true
    }
    schedulePatch({ data: nextData })
  }

  const schedulePatch = (patch) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setSaving(true)
      setError(null)
      setConflict(null)
      const needsReload = needsReloadRef.current
      needsReloadRef.current = false
      try {
        // The patch may include `type` (e.g. when converting to graph) or just
        // `data`. The server PATCH endpoint supports both.
        await CustomAssetManager.updateMaterial(asset.rawId, patch)
        // Our local edit is now persisted — drop the dirty guard so subsequent
        // external changes (e.g. the graph modal) can be adopted again.
        dirtyRef.current = false
        // Only refetch + rebuild on the avatar if a field required it (e.g.
        // texture binding or class promotion). For pure scalar/color/uniform
        // edits the live preview already applied the change in real time,
        // so reloading would only cause a texture-load flicker.
        if (needsReload) reloadMaterialsEverywhere()
      } catch (e) {
        // Version conflict: someone else edited this material since we loaded it.
        // Show the conflict UI and store the pending patch so "Overwrite" can retry.
        if (e.name === 'VersionConflictError') {
          pendingPatchRef.current = patch
          setConflict({ current: e.current, localData: data })
        } else {
          setError(String(e.message ?? e))
        }
      } finally {
        setSaving(false)
      }
    }, 350)
  }

  // Handle "Reload" button: drop local edits and adopt the server version.
  const handleReload = () => {
    if (!conflict?.current) return
    // A queued debounce firing after Reload would silently re-overwrite the
    // server row with the edits the user just chose to drop.
    if (debounceRef.current) clearTimeout(debounceRef.current)
    pendingPatchRef.current = null
    dirtyRef.current = false
    setData(conflict.current.data ?? {})
    setConflict(null)
  }

  // Handle "Overwrite" button: re-PATCH with the server's current version.
  const handleOverwrite = async () => {
    if (!conflict?.current || !pendingPatchRef.current) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSaving(true)
    setError(null)
    setConflict(null)
    try {
      await CustomAssetManager.updateMaterial(asset.rawId, pendingPatchRef.current)
      dirtyRef.current = false
      pendingPatchRef.current = null
    } catch (e) {
      if (e.name === 'VersionConflictError') {
        // pendingPatchRef intentionally kept — the user can Overwrite again.
        setConflict({ current: e.current, localData: data })
      } else {
        setError(String(e.message ?? e))
      }
    } finally {
      setSaving(false)
    }
  }

  // Subscribe to history changes so the Undo/Redo buttons re-render on every
  // push, undo, redo, or asset switch. The snapshot encodes both undo/redo
  // availability as a stable string; useSyncExternalStore re-renders when
  // the string changes.
  useSyncExternalStore(
    history.subscribe,
    () => `${history.canUndo(asset.rawId) ? 'u' : ''}|${history.canRedo(asset.rawId) ? 'r' : ''}`,
  )
  const canU = history.canUndo(asset.rawId)
  const canR = history.canRedo(asset.rawId)

  const doUndo = () => {
    const snap = history.undo(asset.rawId, data)
    if (snap) restoreSnapshot(snap)
  }
  const doRedo = () => {
    const snap = history.redo(asset.rawId, data)
    if (snap) restoreSnapshot(snap)
  }

  // Keyboard shortcuts — Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z or Ctrl+Y = redo.
  // Only fires when the active element isn't a text input / textarea (so it
  // doesn't override the browser's input-level undo).
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) {
        // Allow inputs to handle their own undo. Texture pickers / etc. still
        // work via the toolbar buttons.
        return
      }
      const mod = e.ctrlKey || e.metaKey
      if (!mod) return
      const key = e.key.toLowerCase()
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); doUndo() }
      else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); doRedo() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [asset.rawId, data])

  // Lookup table for binding texture-asset URLs to slot previews.
  const texById = useMemo(() => {
    const m = new Map()
    for (const t of CustomAssetManager.listTextureAssets()) m.set(t.id, t)
    return m
  }, [asset.id, CustomAssetManager.listTextureAssets().length])

  const isToon = asset.materialType === 'toon'
  const isSkin = asset.materialType === 'skin'
  const isGraph = asset.materialType === 'graph'

  const convertToGraph = () => {
    const seeded = {
      version: 1,
      nodes: [
        {
          id: 'out',
          type: 'PrincipledBSDF',
          data: {
            color: data.diffuse ?? [1, 1, 1],
            roughness: data.roughness ?? 0.5,
            metalness: data.metalness ?? 0,
          },
          position: { x: 360, y: 160 },
        },
      ],
      edges: [],
    }
    needsReloadRef.current = true
    schedulePatch({ type: 'graph', data: { ...data, graph: seeded } })
    setData(prev => ({ ...prev, graph: seeded }))
  }

  return (
    <>
      {/* Toolbar — Undo / Redo + Saving status. Sticky at the top of the
          inspector so it's always reachable while scrolling. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '4px 8px',
        borderBottom: `1px solid ${C.border}`,
        background: '#080a10',
      }}>
        <button
          onClick={doUndo}
          disabled={!canU}
          title={canU ? 'Undo (Ctrl+Z)' : 'Nothing to undo'}
          style={toolbarBtn(!canU)}
        >↶</button>
        <button
          onClick={doRedo}
          disabled={!canR}
          title={canR ? 'Redo (Ctrl+Shift+Z)' : 'Nothing to redo'}
          style={toolbarBtn(!canR)}
        >↷</button>
        <div style={{ flex: 1, fontFamily: 'var(--m-font)', fontSize: 10, color: error ? '#f87171' : C.dim, textAlign: 'right' }}>
          {error ? `⚠ ${error}` : (saving ? 'Saving…' : '')}
        </div>
      </div>

      {/* Version conflict bar — shows when another edit wins the race */}
      {conflict && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px',
          borderBottom: `1px solid ${C.border}`,
          background: '#1a1620',
          color: C.text,
          fontSize: 12,
        }}>
          <span style={{ flex: 1 }}>Changed by someone else</span>
          <button
            onClick={handleReload}
            disabled={saving}
            style={{
              padding: '4px 12px',
              borderRadius: 4,
              border: `1px solid ${C.accent}`,
              background: 'transparent',
              color: C.accent,
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.5 : 1,
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            Reload
          </button>
          <button
            onClick={handleOverwrite}
            disabled={saving}
            style={{
              padding: '4px 12px',
              borderRadius: 4,
              border: `1px solid ${C.accent}`,
              background: C.accent,
              color: '#080a10',
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.5 : 1,
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            Overwrite
          </button>
        </div>
      )}

      {/* ── Graph editor (graph material type) ────────────────────────── */}
      {isGraph && (
        <>
          <Panel title="Exposed Parameters" defaultOpen={true}>
            {(() => {
              const params = collectExposedParams(data.graph ?? { nodes: [], edges: [] })
              if (params.length === 0) {
                return (
                  <div style={{
                    fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim,
                    fontStyle: 'italic', padding: '8px 0',
                  }}>
                    No exposed parameters. Open the graph to expose some.
                  </div>
                )
              }
              return params.map(param => {
                const writeParam = (graph, nodeId, handle, value) => ({
                  ...graph,
                  nodes: graph.nodes.map(n =>
                    n.id === nodeId
                      ? { ...n, data: { ...n.data, [handle]: value } }
                      : n
                  ),
                })
                const handleChange = (value) => {
                  // Live preview for numeric uniforms (textures rebind on the
                  // persist → __RELOAD_MATERIALS__ path instead).
                  if (param.type !== 'texture' && typeof window !== 'undefined' && typeof window.__SET_GRAPH_PARAM__ === 'function') {
                    window.__SET_GRAPH_PARAM__(asset.rawId, param.nodeId, param.handle, value)
                  }
                  // Persist (param.handle is the node-data field: value / textureId)
                  set('graph', writeParam(data.graph, param.nodeId, param.handle, value))
                }

                if (param.type === 'texture') {
                  return (
                    <TextureSlotField
                      key={`${param.nodeId}:${param.handle}`}
                      label={param.name}
                      value={param.value}
                      texById={texById}
                      onChange={handleChange}
                      onNavigateToAsset={onNavigateToAsset}
                    />
                  )
                }
                if (param.type === 'float') {
                  return (
                    <NumRow
                      key={`${param.nodeId}:${param.handle}`}
                      label={param.name}
                      v={param.value}
                      onChange={handleChange}
                    />
                  )
                } else if (param.type === 'vec3') {
                  return (
                    <ColorRow
                      key={`${param.nodeId}:${param.handle}`}
                      label={param.name}
                      v={param.value}
                      onChange={handleChange}
                    />
                  )
                } else {
                  return (
                    <Row key={`${param.nodeId}:${param.handle}`} label={param.name}>
                      <div style={{
                        fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim,
                        fontStyle: 'italic',
                      }}>
                        ({param.type} not editable here)
                      </div>
                    </Row>
                  )
                }
              })
            })()}
          </Panel>

          <Panel title="Shader Graph" defaultOpen={false}>
            <div style={{ marginBottom: 8 }}>
              <button
                onClick={() => onOpenGraph?.(asset)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  background: C.accent,
                  border: 'none',
                  color: '#fff',
                  fontFamily: 'var(--m-font)',
                  fontSize: 9,
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  borderRadius: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                Open Material Graph
              </button>
            </div>
          </Panel>
        </>
      )}

      {/* ── Standard material panels (pbr/toon/skin) ──────────────────────── */}
      {!isGraph && (
        <>
          {/* Channels follow Unreal / Arnold / Substance Painter naming so the
              art team can move between tools without re-learning labels. Data
              field keys (diffuseMap, metalnessMap, glossMap, …) stay the same
              to preserve existing material_assets rows; only labels change. */}

          {/* ── Channel Packing (top-level workflow choice — visible by
                default since picking ORM vs individual changes which maps the
                artist authors below). */}
          {!isToon && (
        <Panel title="Channel Packing" defaultOpen={true}>
          <CheckRow
            label="Use ORM packed map"
            v={data.mapPacking === 'orm'}
            onChange={v => set('mapPacking', v ? 'orm' : 'individual')}
          />
          {data.mapPacking === 'orm' && (
            <>
              <Slot label="ORM" slotKey="ormMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
              <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 4 }}>
                R = AO · G = Roughness · B = Metallic (glTF spec)
              </div>
            </>
          )}
          {data.mapPacking !== 'orm' && (
            <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 4 }}>
              Authoring AO · Roughness · Metallic as separate textures
            </div>
          )}
        </Panel>
      )}

      {/* ── Base Color ────────────────────────────────────────────────── */}
      <Panel title="Base Color" defaultOpen={true}>
        <Slot label="Base Color" slotKey="diffuseMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
        <ColorRow label="Tint" v={data.diffuse} onChange={v => set('diffuse', v)} />
        <CheckRow label="Map × Tint" v={!!data.diffuseMapTint} onChange={v => set('diffuseMapTint', v)} />
        <CheckRow label="Vertex color" v={!!data.diffuseMapVertexColor} onChange={v => set('diffuseMapVertexColor', v)} />
      </Panel>

      {/* ── Color Adjustments (Photoshop-style sliders on the Base Color
            sample, applied in-shader before lighting — source texture is
            never mutated). Lives directly under Base Color so the
            relationship is obvious. */}
      <Panel title="Color Adjustments" defaultOpen={false}>
        <NumRow label="Contrast" v={data.textureContrast ?? 1} onChange={v => set('textureContrast', v)} min={0} max={2} step={0.01} />
        <NumRow label="Saturation" v={data.textureSaturation ?? 1} onChange={v => set('textureSaturation', v)} min={0} max={2} step={0.01} />
        <NumRow label="Brightness" v={data.textureValue ?? 1} onChange={v => set('textureValue', v)} min={0} max={2} step={0.01} />
        <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 4 }}>
          Non-destructive · adjusts the sampled Base Color · source texture unchanged
        </div>
      </Panel>

      {/* ── Metallic ──────────────────────────────────────────────────── */}
      {!isToon && !isSkin && (
        <Panel title="Metallic" defaultOpen={true}>
          <div style={{ opacity: data.mapPacking === 'orm' ? 0.4 : 1 }}>
            <Slot label="Metallic" slotKey="metalnessMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
            {data.mapPacking === 'orm' && (
              <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 4 }}>
                Map overridden by ORM packed map · scalar still applies
              </div>
            )}
          </div>
          <NumRow label="Metallic" v={data.metalness ?? 0} onChange={v => set('metalness', v)} min={0} max={1} step={0.01} />
        </Panel>
      )}

      {/* ── Roughness (storage field: glossMap; UI label: Roughness) ──── */}
      {!isToon && !isSkin && (
        <Panel title="Roughness" defaultOpen={true}>
          <div style={{ opacity: data.mapPacking === 'orm' ? 0.4 : 1 }}>
            <Slot label="Roughness" slotKey="glossMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
            {data.mapPacking === 'orm' && (
              <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 4 }}>
                Map overridden by ORM packed map · scalar still applies
              </div>
            )}
          </div>
          <NumRow label="Roughness" v={data.roughness ?? 0.5} onChange={v => set('roughness', v)} min={0} max={1} step={0.01} />
          <CheckRow label="Invert (gloss → roughness)" v={!!data.glossInvert} onChange={v => set('glossInvert', v)} />
        </Panel>
      )}

      {/* ── Specular (advanced, default collapsed) ────────────────────── */}
      {!isToon && !isSkin && (
        <Panel title="Specular" defaultOpen={false}>
          <Slot label="Specular" slotKey="specularIntensityMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
          <NumRow label="Specular" v={data.specularIntensity ?? 1} onChange={v => set('specularIntensity', v)} min={0} max={1} step={0.01} />
          <Slot label="Specular Color" slotKey="specularColorMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
          <ColorRow label="Specular Color" v={data.specularColor} onChange={v => set('specularColor', v)} />
          <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 4 }}>
            F0 reflectance for dielectrics · default 1.0 ≈ 4% (glTF spec)
          </div>
        </Panel>
      )}

      {/* ── Normal ────────────────────────────────────────────────────── */}
      {!isToon && (
        <Panel title="Normal" defaultOpen={true}>
          <Slot label="Normal" slotKey="normalMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
          <NumRow label="Normal Strength" v={data.bumpMapFactor ?? 1} onChange={v => set('bumpMapFactor', v)} min={0} max={2} step={0.05} />
        </Panel>
      )}

      {/* ── Ambient Occlusion ─────────────────────────────────────────── */}
      {!isToon && !isSkin && (
        <Panel title="Ambient Occlusion" defaultOpen={false}>
          <div style={{ opacity: data.mapPacking === 'orm' ? 0.4 : 1 }}>
            <Slot label="Ambient Occlusion" slotKey="aoMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
            <SelectRow label="Channel" v={data.aoMapChannel ?? 'r'} options={[['r','R'],['g','G'],['b','B']]} onChange={v => set('aoMapChannel', v)} />
            <NumRow label="Intensity" v={data.aoIntensity ?? 1} onChange={v => set('aoIntensity', v)} min={0} max={10} step={0.05} />
            {data.mapPacking === 'orm' && (
              <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 4 }}>
                Map overridden by ORM packed map · intensity still applies
              </div>
            )}
          </div>
        </Panel>
      )}

      {/* ── Emission ──────────────────────────────────────────────────── */}
      {!isToon && (
        <Panel title="Emission" defaultOpen={false}>
          <Slot label="Emission" slotKey="emissiveMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
          <ColorRow label="Color" v={data.emissive} onChange={v => set('emissive', v)} />
          <NumRow label="Intensity" v={data.emissiveIntensity ?? 1} onChange={v => set('emissiveIntensity', v)} min={0} max={100} step={0.1} />
        </Panel>
      )}

      {/* ── Opacity ───────────────────────────────────────────────────── */}
      <Panel title="Opacity" defaultOpen={false}>
        <NumRow label="Opacity" v={data.opacity ?? 1} onChange={v => set('opacity', v)} min={0} max={1} step={0.01} />
        <Slot label="Opacity" slotKey="opacityMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
        <SelectRow label="Blend mode" v={data.blendType ?? 3} options={[[3,'None'],[2,'Alpha'],[1,'Additive'],[4,'Premultiplied'],[5,'Multiply']]} onChange={v => set('blendType', Number(v))} />
        <NumRow label="Alpha test" v={data.alphaTest ?? 0} onChange={v => set('alphaTest', v)} min={0} max={1} step={0.01} />
        <CheckRow label="Depth test" v={data.depthTest !== false} onChange={v => set('depthTest', v)} />
        <CheckRow label="Depth write" v={data.depthWrite !== false} onChange={v => set('depthWrite', v)} />
      </Panel>

      {/* ── Subsurface (skin material type only) ──────────────────────── */}
      {isSkin && (
        <Panel title="Subsurface" defaultOpen={true}>
          <Slot label="Thickness" slotKey="thicknessMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
          <ColorRow label="Subsurface Color" v={data.subsurfaceColor} onChange={v => set('subsurfaceColor', v)} />
          <NumRow label="Weight" v={data.subsurfaceAmount ?? 0.5} onChange={v => set('subsurfaceAmount', v)} min={0} max={1} step={0.01} />
          <NumRow label="Roughness" v={data.roughness ?? 0.6} onChange={v => set('roughness', v)} min={0} max={1} step={0.01} />
        </Panel>
      )}

      {/* ── Clear Coat (opt-in, promotes to PhysicalMaterial) ─────────── */}
      {!isToon && !isSkin && (
        <Panel title="Clear Coat" defaultOpen={false}>
          <CheckRow label="Enable" v={!!data.clearcoatEnabled} onChange={v => set('clearcoatEnabled', v)} />
          {data.clearcoatEnabled && (
            <>
              <NumRow label="Weight" v={data.clearcoat ?? 1} onChange={v => set('clearcoat', v)} min={0} max={1} step={0.01} />
              <NumRow label="Roughness" v={data.clearcoatRoughness ?? 0} onChange={v => set('clearcoatRoughness', v)} min={0} max={1} step={0.01} />
              <Slot label="Clear Coat" slotKey="clearcoatMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
              <Slot label="Clear Coat Roughness" slotKey="clearcoatRoughnessMap" data={data} set={set} texById={texById} onNavigateToAsset={onNavigateToAsset} />
              <div style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: C.dim, marginTop: 4 }}>
                Off by default · enabling promotes the material to PhysicalMaterial
              </div>
            </>
          )}
        </Panel>
      )}

      {/* ── Toon (toon material type only) ────────────────────────────── */}
      {isToon && (
        <Panel title="Toon" defaultOpen={true}>
          <NumRow label="Ramp bands" v={data.toonRamp ?? 3} onChange={v => set('toonRamp', v)} min={2} max={8} step={1} />
          <ColorRow label="Outline" v={data.outlineColor} onChange={v => set('outlineColor', v)} />
          <NumRow label="Outline width" v={data.outlineWidth ?? 0} onChange={v => set('outlineWidth', v)} min={0} max={0.1} step={0.001} />
        </Panel>
      )}

      <Panel title="Advanced" defaultOpen={false}>
        <SelectRow label="Cull" v={data.cull ?? 1} options={[[0,'None'],[1,'Back'],[2,'Front'],[3,'Both']]} onChange={v => set('cull', Number(v))} />
        {/* Convert-to-graph is gated behind a feature flag while the
            conversion path is still being validated (ENG-612). Off by default;
            opt in with localStorage 'aos-material-convert-enabled' = 'true'.
            Creating a graph material from scratch (New asset → Material — Graph)
            is unaffected. */}
        {isMaterialGraphConvertEnabled() && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
            <button
              onClick={convertToGraph}
              style={{
                width: '100%',
                padding: '6px 8px',
                background: C.accent,
                border: 'none',
                color: '#fff',
                fontFamily: 'var(--m-font)',
                fontSize: 9,
                fontWeight: 'bold',
                cursor: 'pointer',
                borderRadius: 2,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
              }}
            >
              Convert to Graph Material
            </button>
            <div style={{ fontFamily: 'var(--m-font)', fontSize: 8, color: C.dim, marginTop: 6 }}>
              Creates a shader graph with current values seeded into a Principled BSDF output node.
            </div>
          </div>
        )}
      </Panel>
        </>
      )}
    </>
  )
}

// Slot — TextureSlotField + a collapsible per-map UV transform row.
// `slotKey` is the data field name for the texture UUID (e.g. 'normalMap');
// transforms are persisted under `<slotKey>Offset`, `<slotKey>Tiling`,
// `<slotKey>Rotation`. UV controls only render when the slot has a texture
// bound, to avoid bloat on empty slots.
function Slot({ label, slotKey, data, set, texById, onNavigateToAsset }) {
  const [uvOpen, setUvOpen] = React.useState(false)
  const value = data[slotKey]
  return (
    <>
      <TextureSlotField
        label={label}
        value={value}
        texById={texById}
        onChange={v => set(slotKey, v)}
        onNavigateToAsset={onNavigateToAsset}
      />
      {value && (
        <div style={{ marginTop: -2, marginBottom: 6 }}>
          <button
            onClick={() => setUvOpen(o => !o)}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: C.dim, fontFamily: 'var(--m-font)', fontSize: 9,
              padding: '2px 0',
            }}
          >
            {uvOpen ? '▼' : '▶'} UV transform
          </button>
          {uvOpen && (
            <div style={{ paddingLeft: 12 }}>
              <Vec2Row label="Offset" v={data[slotKey + 'Offset']} onChange={v => set(slotKey + 'Offset', v)} />
              <Vec2Row label="Tiling" v={data[slotKey + 'Tiling']} onChange={v => set(slotKey + 'Tiling', v)} />
              <NumRow label="Rotation" v={data[slotKey + 'Rotation'] ?? 0} onChange={v => set(slotKey + 'Rotation', v)} suffix="°" step={1} />
            </div>
          )}
        </div>
      )}
    </>
  )
}

// ── Section / row primitives ────────────────────────────────────────────────

function Panel({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          background: '#080a10', border: 'none',
          padding: '8px 14px', cursor: 'pointer',
          color: C.text, fontFamily: 'var(--m-font)', fontSize: 10,
          letterSpacing: '.14em', textTransform: 'uppercase',
          textAlign: 'left',
        }}
      >
        <span style={{ color: C.accent, fontSize: 9 }}>{open ? '▼' : '▶'}</span>
        <span>{title}</span>
      </button>
      {open && (
        <div style={{ padding: '8px 14px 10px', background: C.bg }}>
          {children}
        </div>
      )}
    </div>
  )
}

function Row({ label, children }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '90px 1fr',
      alignItems: 'center', gap: 10,
      marginBottom: 6,
    }}>
      <div style={{
        fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{label}</div>
      <div>{children}</div>
    </div>
  )
}

function NumRow({ label, v, onChange, min, max, step = 0.01, suffix = '' }) {
  const [local, setLocal] = useState(String(v ?? 0))
  useEffect(() => { setLocal(String(v ?? 0)) }, [v])
  const commit = () => {
    const n = parseFloat(local)
    if (!Number.isFinite(n)) { setLocal(String(v ?? 0)); return }
    let clamped = n
    if (min != null) clamped = Math.max(min, clamped)
    if (max != null) clamped = Math.min(max, clamped)
    if (clamped !== v) onChange(clamped)
    setLocal(String(clamped))
  }
  const sliderVisible = min != null && max != null
  return (
    <Row label={label}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {sliderVisible && (
          <input
            type="range" min={min} max={max} step={step} value={parseFloat(local) || 0}
            onChange={e => { setLocal(e.target.value); onChange(parseFloat(e.target.value)) }}
            style={{ flex: 1, accentColor: C.accent }}
          />
        )}
        <input
          type="number"
          value={local}
          min={min} max={max} step={step}
          onChange={e => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
          style={numInput}
        />
        {suffix && <span style={{ fontFamily: 'var(--m-mono)', fontSize: 10, color: C.dim }}>{suffix}</span>}
      </div>
    </Row>
  )
}

function Vec2Row({ label, v, onChange }) {
  const arr = Array.isArray(v) && v.length === 2 ? v : [0, 0]
  return (
    <Row label={label}>
      <div style={{ display: 'flex', gap: 6 }}>
        {[0, 1].map(i => (
          <input
            key={i}
            type="number"
            step={0.01}
            value={arr[i] ?? 0}
            onChange={e => {
              const next = [...arr]
              next[i] = parseFloat(e.target.value) || 0
              onChange(next)
            }}
            style={{ ...numInput, flex: 1 }}
          />
        ))}
        <span style={{ fontFamily: 'var(--m-mono)', fontSize: 9, color: C.dim, alignSelf: 'center' }}>UV</span>
      </div>
    </Row>
  )
}

function ColorRow({ label, v, onChange }) {
  const rgb = Array.isArray(v) && v.length === 3 ? v : [1, 1, 1]
  const hex = rgbToHex(rgb)
  return (
    <Row label={label}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="color"
          value={hex}
          onChange={e => onChange(hexToRgb(e.target.value))}
          style={{
            width: 32, height: 22, padding: 0, border: `1px solid ${C.border}`,
            borderRadius: 3, background: 'transparent', cursor: 'pointer',
          }}
        />
        <span style={{ fontFamily: 'var(--m-mono)', fontSize: 10, color: C.text }}>
          {rgb.map(c => c.toFixed(2)).join(', ')}
        </span>
      </div>
    </Row>
  )
}

function CheckRow({ label, v, onChange }) {
  return (
    <Row label={label}>
      <input
        type="checkbox"
        checked={!!v}
        onChange={e => onChange(e.target.checked)}
        style={{ accentColor: C.accent, width: 14, height: 14 }}
      />
    </Row>
  )
}

function SelectRow({ label, v, options, onChange }) {
  return (
    <Row label={label}>
      <select
        value={v}
        onChange={e => onChange(e.target.value)}
        style={selectInput}
      >
        {options.map(([val, lbl]) => (
          <option key={val} value={val}>{lbl}</option>
        ))}
      </select>
    </Row>
  )
}

// ── Color conversion ────────────────────────────────────────────────────────

function rgbToHex(rgb) {
  const to = v => {
    const n = Math.max(0, Math.min(255, Math.round((v ?? 0) * 255)))
    return n.toString(16).padStart(2, '0')
  }
  return '#' + to(rgb[0]) + to(rgb[1]) + to(rgb[2])
}

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex)
  if (!m) return [1, 1, 1]
  return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255]
}

function toolbarBtn(disabled) {
  return {
    width: 24, height: 22,
    background: disabled ? '#0c0e15' : '#1a1d28',
    border: `1px solid ${disabled ? '#1f2129' : '#2a2c36'}`,
    color: disabled ? '#3a3c45' : C.text,
    borderRadius: 3,
    fontFamily: 'var(--m-font)', fontSize: 13, lineHeight: 1,
    cursor: disabled ? 'default' : 'pointer',
    padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }
}

const numInput = {
  width: 64,
  background: '#080a10', border: `1px solid ${C.border}`,
  borderRadius: 3, padding: '3px 6px',
  color: C.text, fontFamily: 'var(--m-mono)', fontSize: 10,
  outline: 'none',
}

const selectInput = {
  width: '100%',
  background: '#080a10', border: `1px solid ${C.border}`,
  borderRadius: 3, padding: '3px 6px',
  color: C.text, fontFamily: 'var(--m-font)', fontSize: 10,
  outline: 'none',
}
